// @ts-nocheck
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  related_module: string;
  related_id: string;
  read: boolean;
  created_at: string;
}

export const useMentionNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, profile } = useAuth();

  const fetchNotifications = async () => {
    if (!user?.id || !profile?.id) {
      console.log('🔔 No user or profile found for notifications');
      return;
    }

    try {
      setLoading(true);
      console.log('🔔 Fetching mention notifications for user:', user.id, 'profile:', profile.id);

      // profile_mentions table doesn't exist in RLevents
      const mentions: any[] = [];
      console.log('🔍 Found pending mentions:', 0, null);

      // Now fetch notifications - get ALL types, not just mentions
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('❌ Error fetching notifications:', error);
        throw error;
      }

      console.log('✅ Fetched notifications:', data?.length || 0, data);
      
      // If we have pending mentions but no notifications, this indicates the notification creation might have failed
      if (mentions && mentions.length > 0 && (!data || data.length === 0)) {
        console.warn('⚠️ Found pending mentions but no notifications. This might indicate notification creation failed.');
        
        // Create missing notifications for pending mentions
        for (const mention of mentions) {
          console.log('🔧 Creating missing notification for mention:', mention.id);
          
          // Get source details based on source_type
          let sourceTitle = 'Unknown Source';
          let authorName = 'Unknown Author';
          let notificationType = 'mention_request';
          let relatedModule = 'profile_mentions';
          
          if (mention.source_type === 'video' && mention.source_id) {
            // Get video details
            const { data: video } = await supabase
              .from('videos')
              .select('title')
              .eq('id', mention.source_id)
              .single();
            
            if (video) {
              sourceTitle = video.title || 'Untitled Video';
            }
            
            // Get author name from mentioned_by
            if (mention.mentioned_by) {
              const { data: authorProfile } = await supabase
                .from('profiles')
                .select('name')
                .eq('id', mention.mentioned_by)
                .single();
              
              authorName = authorProfile?.name || 'Admin';
            }
          }
          
          // Create message based on mention type
          let message = '';
          if (mention.source_type === 'video') {
            message = `Great news! You've been featured in "${sourceTitle}" by ${authorName}. Click to watch now.`;
          } else {
            message = `${authorName} mentioned you in "${sourceTitle}". Please review and approve or decline.`;
          }
          
          // Create the missing notification
          const { error: insertError } = await supabase
            .from('notifications')
            .insert({
              user_id: user.id,
              type: notificationType,
              title: mention.source_type === 'video' ? 'You\'ve been featured in a video!' : 'You have been mentioned',
              message: message,
              related_module: relatedModule,
              related_id: mention.id,
              read: false
            });
          
          if (insertError) {
            console.error('❌ Error creating missing notification:', insertError);
          } else {
            console.log('✅ Created missing notification for mention:', mention.id);
          }
        }
        
        // Refetch notifications after creating missing ones
        const { data: updatedData, error: refetchError } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (!refetchError) {
          setNotifications(updatedData || []);
          const unread = (updatedData || []).filter(n => !n.read).length;
          setUnreadCount(unread);
          console.log('✅ Updated notifications after creating missing ones:', updatedData?.length || 0);
        }
      } else {
        setNotifications(data || []);
        const unread = (data || []).filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      console.log('📖 Marking notification as read:', notificationId);

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('❌ Error marking notification as read:', error);
        throw error;
      }

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      console.log('✅ Notification marked as read');
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    if (user?.id && profile?.id) {
      fetchNotifications();
    }
  }, [user?.id, profile?.id]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead
  };
};
